import { Fragment, type ReactElement, useCallback, useMemo, useState } from 'react'
import { isDefined, isEmpty } from '@terros/common'
import { useTranslation } from '@front/model'
import { useElementHeight } from './useElementHeight'
import { type TDataTableProps } from './types'
import { TableMessage } from './TableMessage'
import { HEADER_FALLBACK_HEIGHT, Row } from './Row'
import { Loading } from './Loading'
import { Header } from './Header'

export const TableContent = <T extends Record<string, unknown> | unknown[]>({
  setColumnWidth,
  ...props
}: TDataTableProps<T>): ReactElement => {
  const {
    records,
    isLoading,
    error,
    columns: effectiveColumns,
    pinFirstColumn,
    pinLastColumn,
    pinnedRows = [],
    showHeader = true,
    sortStatus,
    onSortStatusChange,
    rowExpansion,
    idAccessor,
    nestedTable,
    onRowClick,
    onColumnReorder,
  } = props
  const [expandedRows, setExpandedRows] = useState<string[]>([])
  const { ref: firstHeaderCellRef, height: measuredHeaderHeight } = useElementHeight<HTMLDivElement>()
  const pinnedRowsTopOffset = useMemo(() => {
    if (!showHeader) return 0
    if (isEmpty(measuredHeaderHeight)) return HEADER_FALLBACK_HEIGHT
    return measuredHeaderHeight
  }, [showHeader, measuredHeaderHeight])
  const { t } = useTranslation()

  const isExpandable = useCallback(
    (record: T): boolean => {
      if (!rowExpansion) return false
      if (!rowExpansion.expandable || rowExpansion.expandable({ record })) return true
      return false
    },
    [rowExpansion]
  )

  const renderTableRow = useCallback(
    (record: T, rowIndex: number, isPinned?: boolean) => {
      const rowId = `row-${idAccessor(record)}`
      const isRowExpandable = isExpandable(record)
      const isExpanded = expandedRows.includes(rowId)

      return (
        <Fragment key={rowId}>
          <Row
            isExpanded={isExpanded}
            isExpandable={isRowExpandable}
            isPinned={isPinned}
            isStriped={rowIndex % 2 === 0}
            pinLastColumn={pinLastColumn}
            pinnedTopOffset={pinnedRowsTopOffset}
            nestedTable={nestedTable}
            record={record}
            rowIndex={rowIndex}
            showExpansionChevron={rowExpansion?.withChevron}
            visibleColumns={effectiveColumns}
            onClick={
              isRowExpandable || isDefined(onRowClick)
                ? () => {
                    if (isRowExpandable) {
                      setExpandedRows((prev) => {
                        if (prev.includes(rowId)) {
                          return prev.filter((id) => id !== rowId)
                        }
                        if (rowExpansion?.allowMultiple === false) return [rowId]
                        return [...prev, rowId]
                      })
                    } else {
                      onRowClick?.(record)
                    }
                  }
                : undefined
            }
          />
          {rowExpansion && isExpanded && rowExpansion.content({ record })}
        </Fragment>
      )
    },
    [
      effectiveColumns,
      expandedRows,
      idAccessor,
      isExpandable,
      nestedTable,
      onRowClick,
      pinLastColumn,
      pinnedRowsTopOffset,
      rowExpansion,
    ]
  )

  const noRecords = !isLoading && isEmpty(records) && isEmpty(pinnedRows)

  return (
    <>
      {showHeader && (
        <Header
          columns={effectiveColumns}
          firstCellRef={firstHeaderCellRef}
          pinFirstColumn={pinFirstColumn}
          setColumnWidth={setColumnWidth}
          sortStatus={sortStatus}
          onColumnReorder={onColumnReorder}
          onSortStatusChange={onSortStatusChange}
        />
      )}
      {isLoading && <Loading nested={isNestedDrillDown(nestedTable)} span={effectiveColumns.length} />}
      {noRecords && isEmpty(error) && <TableMessage span={effectiveColumns.length} message={t('No records found')} />}
      {error && <TableMessage span={effectiveColumns.length} message={error} />}
      {!isLoading && (
        <>
          {pinnedRows.map((record, rowIndex) => renderTableRow(record, rowIndex, true))}
          {records.map((record, rowIndex) => renderTableRow(record, rowIndex))}
        </>
      )}
    </>
  )
}

const isNestedDrillDown = (nestedTable: boolean | number | undefined): boolean => {
  if (nestedTable === true) return true
  if (typeof nestedTable === 'number' && nestedTable > 0) return true
  return false
}
